import { FC, useEffect, useState } from 'react';
import { CatList } from 'blocks-nextjs/blocks-react/components/List';
import { CatListItem } from 'blocks-nextjs/blocks-react/components/ListItem';
import { CatSideMenuItem } from 'blocks-nextjs/blocks-react/components/SideMenuItem';
import { BlocksListItem } from 'blocks-nextjs/blocks-web-components/components/list-item/list-item';
import useURLParams from 'hooks/useURLParams';
import cx from 'classnames';
import { debounce } from '@cat-ecom/common/utils/commonUtils';
import { DEBOUNCE_TIME_MILLISECONDS } from 'constants/common';
import { PurifiedHtml } from 'components/purified-html';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import {
  ComponentsType,
  GroupPartsType,
  MenuIndex,
  SystemDataResponseType
} from 'types/parts-diagram';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import layout_styles from '../../../parts-layout.module.scss';

interface PartsMenuListProps {
  systemsData: SystemDataResponseType[];
  menuIndex: MenuIndex[];
  scrollRefs: React.MutableRefObject<unknown>;
  handleGroupItemClick: (groupItem: GroupPartsType) => void;
  handleComponentClick: (components: ComponentsType) => void;
}

export const MenuList: FC<PartsMenuListProps> = ({
  systemsData,
  menuIndex,
  scrollRefs,
  handleGroupItemClick,
  handleComponentClick
}) => {
  const { setParamsWithWindows, deleteParams, checkParamsHas } = useURLParams();
  const [sysIndex, setSysIndex] = useState(-1);
  const [compIndex, setCompIndex] = useState(-1);
  const [groupIndex, setGroupIndex] = useState(-1);
  const { handleSelectedPart, selectedPart } = usePartDiagram();

  const getSelectedMenu = () => {
    const {
      compId,
      ieSystemControlNumber: ieSys,
      partNumber: pNumber,
      partType = 'part',
      sysId
    } = selectedPart || {};

    return (menuIndex ?? []).filter((x) => {
      const {
        partNumber = '',
        ieSystemControlNumber = '',
        id = ''
      } = x.data || {};
      switch (partType) {
        case 'part':
          return (
            partNumber === pNumber &&
            (ieSystemControlNumber ? ieSystemControlNumber === ieSys : true)
          );
        case 'component':
          return id === `${sysId}${compId}`;
        case 'system':
          return id === `${sysId}${sysId}`;
      }
    })?.[0];
  };

  useEffect(() => {
    if (!selectedPart) {
      return;
    }
    const {
      indexArr: [i, j, k] = [-1, -1, -1],
      data: { partNumber = '', ieSystemControlNumber = '', id }
    } = getSelectedMenu() || {};
    const scrollRefId =
      selectedPart?.partType === 'part'
        ? `${partNumber}${ieSystemControlNumber ?? ''}`
        : id;

    const scrollToElement = () =>
      scrollRefId &&
      scrollRefs?.current[scrollRefId]?.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });

    debounce(scrollToElement, DEBOUNCE_TIME_MILLISECONDS);
    i >= 0 && setSysIndex(i);
    j >= 0 && setCompIndex(j);
    k >= 0 && setGroupIndex(k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuIndex, scrollRefs, selectedPart]);

  const handleSysClick = (i: number, id: string) => {
    deleteParams('componentId');
    deleteParams('ieSystemControlNumber');
    setParamsWithWindows('systemId', id);
    setSysIndex(i);
    handleSelectedPart(null);
  };

  const handleCompClick = (
    e: React.MouseEvent<BlocksListItem, MouseEvent>,
    systemId: string,
    subItem: ComponentsType,
    j: number
  ) => {
    e.stopPropagation();
    if (!checkParamsHas(systemId)) {
      deleteParams('componentId');
      deleteParams('ieSystemControlNumber');
      setParamsWithWindows('systemId', systemId);
    }
    checkParamsHas('ieSystemControlNumber') &&
      deleteParams('ieSystemControlNumber');
    setParamsWithWindows('componentId', subItem.id);
    setCompIndex(j);
    handleSelectedPart(null);
    handleComponentClick(subItem);
  };

  const handleGroupClick = (
    e: React.MouseEvent<BlocksListItem, MouseEvent>,
    systemId: string,
    componentId: string,
    groupItem: GroupPartsType,
    k: number
  ) => {
    e.stopPropagation();
    if (!checkParamsHas(systemId)) {
      deleteParams('componentId');
      deleteParams('ieSystemControlNumber');
      setParamsWithWindows('systemId', systemId);
    }
    !checkParamsHas(componentId) &&
      setParamsWithWindows('componentId', componentId);
    setParamsWithWindows(
      'ieSystemControlNumber',
      groupItem.ieSystemControlNumber
    );
    setGroupIndex(k);
    handleSelectedPart(null);
    handleGroupItemClick(groupItem);
  };

  return (
    <>
      {systemsData?.length > 0 &&
        systemsData?.map((systemsDataItem, i) => {
          const {
            id: sysId = '',
            description = '',
            components = []
          } = systemsDataItem || {};
          return (
            <CatSideMenuItem
              isActive={i === sysIndex}
              key={`${sysId}_${description}`}
              ref={scrollRefs.current[`${sysId}${sysId}`]}
              className={cx(
                'border-bottom cat-u-margin-bottom-none',
                layout_styles['parts-diagram-menu-list']
              )}
              onClick={() => handleSysClick(i, sysId)}
            >
              {description}
              {components.length > 0 && (
                <CatList slot="items" singleOpen={true}>
                  {components.map((subItem, j) => {
                    const {
                      id: compId = '',
                      description = '',
                      groupParts = []
                    } = subItem || {};
                    const itemSize = 50;
                    const maxHeight = 400;
                    const totalHeight = groupParts.length
                      ? Math.min(groupParts.length * itemSize, maxHeight)
                      : 50;
                    return (
                      <CatListItem
                        isActive={j === compIndex}
                        key={`${compId}_${description}`}
                        ref={scrollRefs.current[`${sysId}${compId}`]}
                        className={layout_styles['parts-diagram-sub-menu']}
                        onClick={(e) =>
                          handleCompClick(e, systemsDataItem.id, subItem, j)
                        }
                      >
                        {description}
                        <CatList slot="items">
                          <div
                            style={{
                              height: totalHeight,
                              width: '100%'
                            }}
                            data-testid="groupItem"
                          >
                            <AutoSizer>
                              {({ height, width }) => {
                                return (
                                  <List
                                    height={totalHeight}
                                    itemCount={groupParts.length}
                                    itemSize={itemSize}
                                    width={width}
                                  >
                                    {({ index, style }) => {
                                      const groupItem = groupParts[index];

                                      const {
                                        name = '',
                                        partNumber = '',
                                        ieSystemControlNumber = ''
                                      } = groupItem;
                                      return (
                                        <div
                                          key={`${compId}_${partNumber}_${ieSystemControlNumber}`}
                                          style={style}
                                        >
                                          <CatListItem
                                            ref={
                                              scrollRefs.current[
                                                `${partNumber}${ieSystemControlNumber ?? ''}`
                                              ]
                                            }
                                            isActive={index === groupIndex}
                                            className={
                                              layout_styles[
                                                'parts-diagram-super-sub-menu'
                                              ]
                                            }
                                            onClick={(e) =>
                                              handleGroupClick(
                                                e,
                                                systemsDataItem.id,
                                                subItem.id,
                                                groupItem,
                                                index
                                              )
                                            }
                                          >
                                            <PurifiedHtml
                                              htmlString={`${partNumber} : ${name}`}
                                            />
                                          </CatListItem>
                                        </div>
                                      );
                                    }}
                                  </List>
                                );
                              }}
                            </AutoSizer>
                          </div>
                        </CatList>
                      </CatListItem>
                    );
                  })}
                </CatList>
              )}
            </CatSideMenuItem>
          );
        })}
    </>
  );
};
